import { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useToolDefinitions } from '@/hooks/use-tool-definitions';
import { useSetIntentChecks } from '@/hooks';
import { getApiErrorMessage } from '@/lib/utils';
import { PillButton } from '@/modules/simulator/ui/pill-button';
import { Section } from '@/modules/back-testing/datasets/ui/section';
import { AddButton } from '@/modules/back-testing/datasets/ui/add-button';
import { ToolPicker } from '@/modules/back-testing/datasets/tools/tool-picker';
import { ToolRefChip } from '@/modules/back-testing/datasets/tools/tool-ref-chip';
import { useToolCatalogue } from '@/modules/back-testing/datasets/tools/use-tool-catalogue';
import type { ToolFilter } from '@/modules/back-testing/datasets/tools/tool-filters';
import { SourcePicker } from '@/modules/back-testing/datasets/sources/source-picker';
import { SourceChip } from '@/modules/back-testing/datasets/sources/source-chip';
import { SOURCE_KIND_LABEL, addSource, sourceOptionsOf } from '@/modules/back-testing/datasets/sources/source-kinds';
import { useSourceOptions } from '@/modules/back-testing/datasets/sources/use-source-options';
import type { IntentChecks, ToolRef } from '@/services/accountIntentConfig.service';

/**
 * Intents › Active: what every reply Kim sends on its own for one intent must do — the tools it
 * calls, the tools it never calls, the sources it draws on. The Automation logs check each reply against
 * these. Tools and sources are picked as a dataset's answer key picks them; no arguments, since the
 * checks hold for every ticket of the intent.
 */

const EMPTY: IntentChecks = {
	requiredTools: [],
	forbiddenTools: [],
	sources: { sopIds: [], workflowIds: [], knowledgeIds: [] }
};

type ToolList = 'requiredTools' | 'forbiddenTools';
type View = { kind: 'checks' } | { kind: 'tool'; list: ToolList } | { kind: 'source' };

/** The picker's "already in this dataset" group has no place here. */
const NO_SUGGESTIONS: ReadonlySet<string> = new Set();

const sameRef = (a: ToolRef, b: ToolRef) => a.name === b.name && a.provider === b.provider;

const TOOL_VIEW: Record<ToolList, { title: string; description: string }> = {
	requiredTools: { title: 'A tool it must call', description: 'Every automated reply for this intent calls it at least once.' },
	forbiddenTools: { title: 'A tool it must never call', description: 'An automated reply for this intent that calls it fails.' }
};

interface IntentChecksSheetProps {
	/** The intent being set; null when the sheet is closed. */
	intent: { id: string; name: string } | null;
	/** Its checks as saved; absent when it has none. */
	saved: IntentChecks | undefined;
	/** Opened right after the intent was switched on, so the sheet says why it opened. */
	justEnabled: boolean;
	/** True for a view-only user: the checks show, nothing changes. */
	readOnly: boolean;
	onClose: () => void;
}

export function IntentChecksSheet({ intent, saved, justEnabled, readOnly, onClose }: IntentChecksSheetProps) {
	return (
		<Sheet open={intent !== null} onOpenChange={(open) => !open && onClose()}>
			<SheetContent className='flex w-[560px] flex-col gap-0 sm:max-w-[560px]'>
				{intent && (
					// Keyed by intent, so opening another intent starts from its own saved checks.
					<ChecksEditor
						key={intent.id}
						intent={intent}
						saved={saved ?? EMPTY}
						justEnabled={justEnabled}
						readOnly={readOnly}
						onClose={onClose}
					/>
				)}
			</SheetContent>
		</Sheet>
	);
}

function ChecksEditor({
	intent,
	saved,
	justEnabled,
	readOnly,
	onClose
}: Omit<IntentChecksSheetProps, 'intent' | 'saved'> & { intent: { id: string; name: string }; saved: IntentChecks }) {
	// Read only once a sheet opens, so the Intents tab does not load every SOP, workflow and source.
	const sourceOptions = useSourceOptions();
	const [draft, setDraft] = useState<IntentChecks>(saved);
	const [view, setView] = useState<View>({ kind: 'checks' });
	const { data: definitions = [] } = useToolDefinitions();
	const setChecks = useSetIntentChecks();
	const names = new Map(sourceOptions.map((option) => [option.id, option.name]));
	const changed = JSON.stringify(draft) !== JSON.stringify(saved);

	const addTool = (list: ToolList, tool: ToolRef) => {
		const other: ToolList = list === 'requiredTools' ? 'forbiddenTools' : 'requiredTools';
		setDraft((current) => ({
			...current,
			// A tool cannot be both: picking it for one list takes it off the other.
			[other]: current[other].filter((entry) => !sameRef(entry, tool)),
			[list]: current[list].some((entry) => sameRef(entry, tool)) ? current[list] : [...current[list], tool]
		}));
		setView({ kind: 'checks' });
	};

	async function save() {
		try {
			await setChecks.mutateAsync({ intentId: intent.id, checks: draft });
			toast.success('Automation checks saved');
			onClose();
		} catch (err) {
			toast.error(getApiErrorMessage(err, 'Failed to save the automation checks'));
		}
	}

	if (view.kind === 'tool') {
		return <ToolStep list={view.list} onBack={() => setView({ kind: 'checks' })} onPick={(tool) => addTool(view.list, tool)} />;
	}
	if (view.kind === 'source') {
		return (
			<>
				<StepHeader title='A source it must use' description='Every automated reply for this intent draws on it.' onBack={() => setView({ kind: 'checks' })} />
				<SourcePicker
					options={sourceOptions}
					usedByRun={[]}
					chosen={draft.sources}
					onPick={(option) => {
						setDraft((current) => ({ ...current, sources: addSource(current.sources, option) }));
						setView({ kind: 'checks' });
					}}
				/>
			</>
		);
	}

	const toolSection = (list: ToolList, title: string, hint: string, empty: string) => (
		<Section title={title} hint={hint} action={!readOnly && <AddButton label='Add tool' onClick={() => setView({ kind: 'tool', list })} />}>
			{draft[list].length > 0 ? (
				<div className='flex flex-wrap gap-1.5'>
					{draft[list].map((tool) => (
						<ToolRefChip
							key={`${tool.provider ?? ''}:${tool.name}`}
							tool={tool}
							definitions={definitions}
							onRemove={readOnly ? undefined : () => setDraft((current) => ({ ...current, [list]: current[list].filter((entry) => !sameRef(entry, tool)) }))}
						/>
					))}
				</div>
			) : (
				<p className='text-xs leading-4 text-gray-800'>{empty}</p>
			)}
		</Section>
	);

	const sources = sourceOptionsOf(draft.sources, names);

	return (
		<>
			<SheetHeader className='border-b border-gray-500 px-5 pt-5 pb-4'>
				<SheetTitle className='text-[15px]'>
					Automation checks · <span className='capitalize'>{intent.name}</span>
				</SheetTitle>
				<SheetDescription className='text-[12px]'>
					{justEnabled
						? 'Automation is on for this intent. Pick what every reply Kim sends on its own for it must do; the Automation logs flag each reply that does not.'
						: 'What every reply Kim sends on its own for this intent must do. The Automation logs flag each reply that does not.'}
				</SheetDescription>
			</SheetHeader>
			<div className='flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-5 py-5'>
				{toolSection('requiredTools', 'Must call', 'Each of these tools is called at least once, with any arguments.', 'No tool is required.')}
				{toolSection('forbiddenTools', 'Must never call', 'A reply that calls any of these fails its checks.', 'No tool is forbidden.')}
				<Section
					title='Must use'
					hint='The SOPs, workflows and knowledge a reply draws on. Using more is fine; missing one is not.'
					action={!readOnly && <AddButton label='Add source' onClick={() => setView({ kind: 'source' })} />}
				>
					{sources.length > 0 ? (
						<div className='flex flex-wrap gap-1.5'>
							{sources.map((option) => (
								<SourceChip
									key={`${option.kind}:${option.id}`}
									kind={SOURCE_KIND_LABEL[option.kind]}
									value={option.name}
									onRemove={
										readOnly
											? undefined
											: () =>
													setDraft((current) => ({
														...current,
														sources: { ...current.sources, [option.kind]: current.sources[option.kind].filter((id) => id !== option.id) }
													}))
									}
								/>
							))}
						</div>
					) : (
						<p className='text-xs leading-4 text-gray-800'>No source is required.</p>
					)}
				</Section>
			</div>
			<div className='flex shrink-0 items-center justify-end gap-2 border-t border-gray-500 px-5 py-3'>
				<PillButton variant='ghost' onClick={onClose}>
					{justEnabled && !changed ? 'Skip for now' : readOnly ? 'Close' : 'Cancel'}
				</PillButton>
				{!readOnly && (
					<PillButton variant='primary' disabled={!changed || setChecks.isPending} onClick={() => void save()}>
						{setChecks.isPending ? 'Saving…' : 'Save checks'}
					</PillButton>
				)}
			</div>
		</>
	);
}

function StepHeader({ title, description, onBack }: { title: string; description: string; onBack: () => void }) {
	return (
		<div className='flex shrink-0 items-start gap-2 border-b border-gray-500 px-3 py-2.5 pr-12'>
			<Button variant='ghost' size='icon' className='size-7 shrink-0' aria-label='Back to the checks' onClick={onBack}>
				<ArrowLeft className='size-4' />
			</Button>
			<div className='flex min-w-0 flex-col gap-0.5 pt-0.5'>
				<SheetTitle className='truncate text-[13px] leading-4.5 font-medium text-text-heading'>{title}</SheetTitle>
				<SheetDescription className='text-xs leading-4 text-gray-800'>{description}</SheetDescription>
			</div>
		</div>
	);
}

/** Picking one of the account's tools, as the dataset's tool editor does. */
function ToolStep({ list, onBack, onPick }: { list: ToolList; onBack: () => void; onPick: (tool: ToolRef) => void }) {
	const [query, setQuery] = useState('');
	const [filter, setFilter] = useState<ToolFilter>({ provider: null, access: list === 'forbiddenTools' ? 'write' : 'all' });
	const [picked, setPicked] = useState<string | null>(null);
	const catalogue = useToolCatalogue(filter, query, NO_SUGGESTIONS);
	const pickedTool = picked === null ? null : (catalogue.byKey.get(picked) ?? null);
	return (
		<div className='@container flex min-h-0 flex-1 flex-col'>
			<StepHeader {...TOOL_VIEW[list]} onBack={onBack} />
			<ToolPicker
				catalogue={catalogue}
				query={query}
				onQueryChange={setQuery}
				filter={filter}
				onFilterChange={setFilter}
				chosen={picked}
				chosenTool={pickedTool}
				onChoose={setPicked}
			/>
			<div className='flex shrink-0 items-center justify-end gap-2 border-t border-gray-500 px-4 py-3'>
				<PillButton variant='ghost' onClick={onBack}>
					Cancel
				</PillButton>
				<PillButton variant='primary' disabled={!pickedTool} onClick={() => pickedTool && onPick({ name: pickedTool.tool, provider: pickedTool.provider })}>
					Add tool
				</PillButton>
			</div>
		</div>
	);
}
